import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../api";
import { SESSION_KEY, useSession } from "../auth/session";
import { Card, Field, InlineAlert, useToast } from "../company-ui";
import { rolesOf } from "./roles";

/**
 * Sign-in in Administration (ALN-128, sign-in-methods.md B.3 and B.4): the People table's Sign-in
 * column, its row actions and the panels under a row, and Settings › Sign-in.
 */

export interface SignInSummary {
  method: "password" | "pin"; temporary: boolean; twoStep: "on" | "needed" | "off"; locked: boolean; pinPaused: boolean; shared: boolean;
  lockedUntil?: string | null; temporaryGivenAt?: string | null; temporaryUntil?: string | null;
}
const DAY_MS = 24 * 60 * 60 * 1000;
const lockTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const givenDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
/** "Given Oct 23, 2026. Works once, for 7 more days." (sign-in-methods.md B.3), or that it has expired. */
function temporaryLine(given: string, until: string): string {
  const left = new Date(until).getTime() - Date.now();
  // past its deadline the PIN no longer signs in (temporary_expired), so never say it works
  if (left <= 0) return `Given ${givenDate(given)}. It has expired, so give them a new one.`;
  const days = Math.max(1, Math.ceil(left / DAY_MS));
  return `Given ${givenDate(given)}. Works once, for ${days} more ${days === 1 ? "day" : "days"}.`;
}
export interface SignInMember { id: string; name: string; email: string; username?: string; contactEmail?: string | null; role: string; roles?: string[]; status: "active" | "invited" | "deactivated"; you: boolean; signIn?: SignInSummary }

export interface Settings { pinRoles: string[]; pinMinLength: number; twoStepScope: "off" | "owners_controllers" | "roles" | "everyone"; twoStepRoles: string[]; sharedIdleMinutes: number; version: number }
interface SettingsResponse { settings: Settings; activeOwners: number; canEdit: boolean; thisComputerShared: boolean }

export const SETTINGS_KEY = ["admin", "settings", "sign-in"] as const;
export const useSignInSettings = () => useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<SettingsResponse>("/admin/settings/sign-in") });

const ROLES = ["owner", "controller", "winemaker", "cellar", "estimator", "viewer"];
const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const first = (name: string) => name.split(/\s+/)[0] || name;
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Whether a role may sign in with a PIN at this winery (an owner only with a second active owner, H18). */
export function pinAllowedFor(role: string, s: SettingsResponse | undefined): boolean {
  if (!s) return false;
  return s.settings.pinRoles.includes(role) && (role !== "owner" || s.activeOwners >= 2);
}

/* ------------------------------------------------------------ the Sign-in column */

export function SignInCell({ u }: { u: SignInMember }) {
  const s = u.signIn;
  if (u.status === "invited") return <span className="muted small">Not set up yet. They choose a password from the invitation.</span>;
  if (!s) return null;
  const method = s.method === "pin" ? (s.temporary ? "Temporary PIN" : "PIN") : s.temporary ? "Password (temporary)" : "Password";
  return (
    <div className="stack">
      <span>{method}</span>
      {s.temporaryGivenAt && s.temporaryUntil && <span className="muted small">{temporaryLine(s.temporaryGivenAt, s.temporaryUntil)}</span>}
      <span className="muted small">{s.twoStep === "on" ? "Two-step: on" : s.twoStep === "needed" ? "Two-step: off, sets it up at next sign-in" : "Two-step: off"}</span>
      {s.locked && <span className="signin-bad small">{s.lockedUntil ? `Locked until ${lockTime(s.lockedUntil)}` : "Locked after wrong tries"}</span>}
      {s.pinPaused && <span className="signin-bad small">PIN paused after 15 wrong PINs</span>}
    </div>
  );
}

export type Panel =
  | { kind: "pin-choice" | "pin-together" | "two-step-off"; reset: boolean }
  | { kind: "temp-pin"; secret: string }
  | { kind: "temp-password"; secret: string; switched: boolean };

/** The row's sign-in buttons (B.3). None on your own row; on an owner's or another controller's row, a controller sees only Unlock (H17, TC-AUTH-037). */
export function SignInActions({ u, settings, onPanel, onUnlock }: { u: SignInMember; settings: SettingsResponse | undefined; onPanel: (p: Panel) => void; onUnlock: () => void }) {
  const { data } = useSession();
  const s = u.signIn;
  if (u.you || !s || u.status !== "active") return null;
  const owner = rolesOf(data ?? {}).includes("owner");
  const held = rolesOf(u);
  const full = owner || (!held.includes("owner") && !held.includes("controller"));
  return (
    <>
      {full && s.method === "password" && held.every(r => pinAllowedFor(r, settings)) && <button className="btn btn--ghost btn--sm" type="button" onClick={() => onPanel({ kind: "pin-choice", reset: false })}>Set PIN</button>}
      {full && s.method === "password" && <button className="btn btn--ghost btn--sm" type="button" onClick={() => onPanel({ kind: "temp-password", secret: "", switched: false })}>Reset password</button>}
      {full && s.method === "pin" && <button className="btn btn--ghost btn--sm" type="button" onClick={() => onPanel({ kind: "pin-choice", reset: true })}>Reset PIN</button>}
      {full && s.method === "pin" && <button className="btn btn--ghost btn--sm" type="button" onClick={() => onPanel({ kind: "temp-password", secret: "", switched: true })}>Switch to password</button>}
      {(s.locked || s.pinPaused) && <button className="btn btn--ghost btn--sm" type="button" onClick={onUnlock}>Unlock</button>}
      {owner && s.twoStep === "on" && <button className="btn btn--ghost btn--sm" type="button" onClick={() => onPanel({ kind: "two-step-off", reset: false })}>Turn off two-step</button>}
    </>
  );
}

/* ------------------------------------------------------------ the panel under a row */

function PanelBox({ heading, children }: { heading: string; children: ReactNode }) {
  const id = useId();
  const ref = useRef<HTMLElement>(null);
  // focus moves into the panel when it opens
  useEffect(() => { ref.current?.querySelector<HTMLElement>("input, button")?.focus(); }, [heading]);
  return (
    <section className="signin-panel" aria-labelledby={id} ref={ref}>
      <h3 className="signin-panel__t" id={id}>{heading}</h3>
      {children}
    </section>
  );
}

export function SignInPanel({ u, panel, min, setPanel, onChanged }: { u: SignInMember; panel: Panel; min: number; setPanel: (p: Panel | null) => void; onChanged: () => void }) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f = first(u.name);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (err) { setError(message(err)); }
    setBusy(false);
  };
  const alert = error && <InlineAlert variant="error" title={error} />;

  if (panel.kind === "pin-choice") {
    return (
      <PanelBox heading={panel.reset ? `Reset the PIN for ${u.name}` : `Set a PIN for ${u.name}`}>
        <p className="m0">{panel.reset ? "Their current PIN stops working." : `${f} will sign in with their username and a PIN instead of a password.`} Anyone signed in as {u.name} is signed out. Choose how:</p>
        <div className="signin-choices">
          <button type="button" className="signin-choice" disabled={busy} onClick={() => setPanel({ kind: "pin-together", reset: panel.reset })}>
            <b>Set it together now</b><span className="small muted">{f} is here with you and types their own PIN on this computer, so only {f} knows it.</span>
          </button>
          <button type="button" className="signin-choice" disabled={busy} onClick={() => void run(async () => {
            const r = await api<{ temporaryPin: string }>(`/admin/members/${u.id}/pin`, { body: { mode: "temporary" } });
            setPanel({ kind: "temp-pin", secret: r.temporaryPin }); onChanged();
          })}>
            <b>Give a temporary PIN</b><span className="small muted">You'll see a 6-digit PIN to give {f} in person. It works once, within 7 days, and {f} chooses their own PIN at the next sign-in.</span>
          </button>
        </div>
        {alert}
        <div className="acct-acts"><button type="button" className="btn" onClick={() => setPanel(null)}>Not now</button></div>
      </PanelBox>
    );
  }

  if (panel.kind === "pin-together") {
    const submit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const d = new FormData(e.currentTarget);
      void run(async () => {
        await api(`/admin/members/${u.id}/pin`, { body: { mode: "together", pin: d.get("pin"), confirm: d.get("confirm") } });
        toast(panel.reset ? `PIN saved for ${u.name}.` : `PIN saved. ${f} signs in with it from now on.`);
        setPanel(null); onChanged();
      });
    };
    return (
      <PanelBox heading={panel.reset ? `Reset the PIN for ${u.name}` : `Set a PIN for ${u.name}`}>
        <p className="m0">Do this with {f}, in person. Hand {f} the keyboard so only {f} knows the PIN: {min} to 6 numbers, not all the same and not in a row like 1234.{panel.reset ? "" : ` After this, ${f} signs in with the PIN instead of a password.`}</p>
        <form className="fields fields--narrow" onSubmit={submit} noValidate>
          <Field label="New PIN"><input className="input input--touch acct-pin" name="pin" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" /></Field>
          <Field label="Type it again"><input className="input input--touch acct-pin" name="confirm" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" /></Field>
          {alert}
          <div className="field acct-acts">
            <button className="btn btn--primary" type="submit" disabled={busy}>Save PIN</button>
            <button className="btn" type="button" onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </form>
      </PanelBox>
    );
  }

  if (panel.kind === "temp-pin") {
    return (
      <PanelBox heading={`Temporary PIN for ${u.name}`}>
        <p className="signin-secret" data-testid="temporary-secret">{panel.secret.replace(/^(\d{3})(\d{3})$/, "$1 $2")}</p>
        <p className="m0">Give this to {f} in person. It works once, within 7 days. They'll choose their own PIN when they sign in. It isn't shown again.</p>
        <div className="acct-acts"><button type="button" className="btn btn--primary" onClick={() => setPanel(null)}>Done</button></div>
      </PanelBox>
    );
  }

  if (panel.kind === "temp-password") {
    // Reset password and Switch to password act at once (B.3): the password is made and shown once
    if (!panel.secret) {
      return <TemporaryPasswordStart u={u} switched={panel.switched} onMade={secret => { setPanel({ ...panel, secret }); onChanged(); }} onClose={() => setPanel(null)} />;
    }
    return (
      <PanelBox heading={`Temporary password for ${u.name}, shown once:`}>
        <p className="signin-secret" data-testid="temporary-secret">{panel.secret}</p>
        <p className="m0">Give it to {f} in person. {f} chooses a new password at the next sign-in.{panel.switched ? ` ${f}'s PIN no longer works.` : ""} {f}'s other sessions have ended.</p>
        <div className="acct-acts"><button type="button" className="btn btn--primary" onClick={() => setPanel(null)}>Done</button></div>
      </PanelBox>
    );
  }

  // turn off two-step, with a reason (B.3)
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const reason = String(new FormData(e.currentTarget).get("reason") ?? "").trim();
    if (!reason) { setError("Say why. It's recorded in the audit log."); return; }
    void run(async () => {
      await api(`/admin/members/${u.id}/two-step`, { method: "DELETE", body: { reason } });
      toast(`Two-step is off for ${u.name}.`);
      setPanel(null); onChanged();
    });
  };
  return (
    <PanelBox heading={`Turn off two-step for ${u.name}?`}>
      <p className="m0">Their authenticator app and backup codes stop working, and anyone signed in as {u.name} is signed out. If your winery requires two-step for them, they'll set it up again the next time they sign in.</p>
      <form className="fields fields--narrow" onSubmit={submit} noValidate>
        <Field label="Why?" hint="It's recorded in the audit log."><input className="input" name="reason" maxLength={500} placeholder="For example: lost their phone" /></Field>
        {alert}
        <div className="field acct-acts">
          <button className="btn btn--destructive" type="submit" disabled={busy}>Turn off two-step</button>
          <button className="btn" type="button" onClick={() => setPanel(null)}>Keep it</button>
        </div>
      </form>
    </PanelBox>
  );
}

function TemporaryPasswordStart({ u, switched, onMade, onClose }: { u: SignInMember; switched: boolean; onMade: (secret: string) => void; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api<{ temporaryPassword: string }>(`/admin/members/${u.id}/temporary-password`, { body: {} }).then(r => onMade(r.temporaryPassword), e => setError(message(e)));
  }, [u.id, onMade]);
  return (
    <PanelBox heading={switched ? `Switch ${u.name} to a password` : `Reset the password for ${u.name}`}>
      {error ? <InlineAlert variant="error" title={error} /> : <p className="m0 muted" aria-busy="true">Making a temporary password…</p>}
      <div className="acct-acts"><button type="button" className="btn" onClick={onClose}>Close</button></div>
    </PanelBox>
  );
}

/* ------------------------------------------------------------ Settings › Sign-in */

const SCOPES: [Settings["twoStepScope"], string, string | null][] = [
  ["off", "Off", "Nobody is asked for a code."],
  ["owners_controllers", "Required for owners and controllers", "The people who approve money and post to QuickBooks."],
  ["roles", "Required for chosen roles", "Pick the roles below."],
  ["everyone", "Required for everyone", null],
];
const reaches = (scope: Settings["twoStepScope"], roles: string[], role: string) =>
  scope === "everyone" || (scope === "owners_controllers" && (role === "owner" || role === "controller")) || (scope === "roles" && roles.includes(role));

type Confirm = { kind: "narrow"; role: string; n: number; patch: Partial<Settings> } | { kind: "widen"; n: number; patch: Partial<Settings> };

export function SignInSettingsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useSignInSettings();
  const people = useQuery({ queryKey: ["users"], queryFn: () => api<{ users: SignInMember[] }>("/users") });
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const s = d?.settings;
  const dis = !d?.canEdit || busy;
  const active = (people.data?.users ?? []).filter(u => u.status === "active");

  const save = async (patch: Partial<Settings>) => {
    if (!s) return;
    setBusy(true); setError(null); setConfirm(null);
    try {
      const r = await api<{ settings: Settings }>("/admin/settings/sign-in", { method: "PATCH", body: { ...patch, expectedVersion: s.version } });
      qc.setQueryData(SETTINGS_KEY, { ...d!, settings: r.settings });
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast("Sign-in settings saved.");
    } catch (err) {
      if (err instanceof ApiError && err.code === "edit_conflict") setConflict(true);
      else setError(message(err));
    }
    setBusy(false);
  };
  const changeScope = (scope: Settings["twoStepScope"], roles = s!.twoStepRoles) => {
    const patch: Partial<Settings> = { twoStepScope: scope, twoStepRoles: roles };
    if (scope === "roles" && !roles.length) patch.twoStepRoles = ["owner", "controller"];
    const n = active.filter(u => u.signIn?.twoStep === "off" && rolesOf(u).some(r => reaches(scope, patch.twoStepRoles!, r)) && !rolesOf(u).some(r => reaches(s!.twoStepScope, s!.twoStepRoles, r))).length;
    if (n > 0) setConfirm({ kind: "widen", n, patch }); else void save(patch);
  };
  const changePin = (role: string, on: boolean) => {
    const pinRoles = on ? [...new Set([...s!.pinRoles, role])] : s!.pinRoles.filter(r => r !== role);
    const n = on ? 0 : active.filter(u => (rolesOf(u) as string[]).includes(role) && u.signIn?.method === "pin").length;
    if (n > 0) setConfirm({ kind: "narrow", role, n, patch: { pinRoles } }); else void save({ pinRoles });
  };
  const thisComputer = async (mark: boolean) => {
    setBusy(true); setError(null);
    try {
      await api("/admin/shared-device", { method: mark ? "POST" : "DELETE", body: {} });
      await qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      await qc.invalidateQueries({ queryKey: SESSION_KEY });
    } catch (err) { setError(message(err)); }
    setBusy(false);
  };

  return (
    <Card title="Sign-in" headingId="signin-settings-h"
      description="Everyone signs in with a username, then a password or a PIN. Reset passwords and PINs, and unlock people, in Users. Changes need an owner and are in the audit log.">
      <div className="card__c stackgap signin-settings">
        {q.isPending && <p className="muted" aria-busy="true">Loading sign-in settings…</p>}
        {q.isError && <InlineAlert variant="error" title={message(q.error)} />}
        {d && !d.canEdit && <InlineAlert variant="info" title="Only an owner can change sign-in settings." />}
        {conflict && (
          <div className="stackgap stackgap--sm">
            <InlineAlert variant="warning" title="Someone else changed these settings. Reload to see their changes, then make yours again." />
            <div><button className="btn" type="button" onClick={() => { setConflict(false); void q.refetch(); }}>Reload</button></div>
          </div>
        )}
        {error && <InlineAlert variant="error" title={error} />}
        {confirm && (
          <section className="signin-panel" aria-labelledby="signin-confirm-h">
            <h3 className="signin-panel__t" id="signin-confirm-h">{confirm.kind === "narrow" ? `Stop PIN sign-in for ${title(confirm.role)}?` : "Turn on two-step for more people?"}</h3>
            <p className="m0">{confirm.kind === "narrow"
              ? `${confirm.n} ${confirm.n === 1 ? "person" : "people"} with that role sign in with a PIN. They'll be signed out and will need a temporary password from you before they can sign in again.`
              : `${confirm.n} ${confirm.n === 1 ? "person" : "people"} will set up two-step sign-in the next time they sign in. They'll need a phone with an authenticator app.`}</p>
            <div className="acct-acts">
              <button className={`btn ${confirm.kind === "narrow" ? "btn--destructive" : "btn--primary"}`} type="button" disabled={busy} onClick={() => void save(confirm.patch)}>{confirm.kind === "narrow" ? "Change it" : "Turn it on"}</button>
              <button className="btn" type="button" onClick={() => setConfirm(null)}>{confirm.kind === "narrow" ? "Keep it" : "Not now"}</button>
            </div>
          </section>
        )}
        {s && d && (
          <>
            <fieldset className="signin-grp">
              <legend className="signin-leg">Two-step sign-in</legend>
              <p className="small muted m0">After the password or PIN, a 6-digit code from an app on the person's phone. Off for a new winery. People set it up at their next sign-in.</p>
              <div className="signin-opts">
                {SCOPES.map(([k, l, sub]) => (
                  <label key={k} className="signin-opt">
                    <input type="radio" name="two-step-scope" value={k} checked={s.twoStepScope === k} disabled={dis} onChange={() => changeScope(k)} />
                    <span><b>{l}</b>{sub && <><br /><span className="small muted">{sub}</span></>}</span>
                  </label>
                ))}
              </div>
              {s.twoStepScope === "roles" && (
                <fieldset className="signin-sub">
                  <legend className="small">Roles that need two-step</legend>
                  <div className="signin-roles">
                    {ROLES.map(r => (
                      <label key={r} className="signin-opt"><input type="checkbox" checked={s.twoStepRoles.includes(r)} disabled={dis}
                        onChange={e => changeScope("roles", e.target.checked ? [...s.twoStepRoles, r] : s.twoStepRoles.filter(x => x !== r))} /><span>{title(r)} needs two-step</span></label>
                    ))}
                  </div>
                </fieldset>
              )}
            </fieldset>
            <fieldset className="signin-grp">
              <legend className="signin-leg">PIN sign-in allowed for</legend>
              <p className="small muted m0">A PIN is quicker but easier to guess, so keep it to people who don't approve money. By default only Cellar, Estimator and Viewer can use one.</p>
              <div className="signin-roles">
                {ROLES.map(r => {
                  const oneOwner = r === "owner" && d.activeOwners < 2;
                  return (
                    <div key={r} className="signin-rolebox">
                      <label className="signin-opt"><input type="checkbox" checked={s.pinRoles.includes(r)} disabled={dis || oneOwner} aria-describedby={oneOwner ? "pin-owner-note" : undefined}
                        onChange={e => changePin(r, e.target.checked)} /><span>{title(r)}</span></label>
                      {oneOwner && <span className="small muted" id="pin-owner-note">Needs a second owner</span>}
                    </div>
                  );
                })}
              </div>
              {s.pinRoles.includes("owner") && d.activeOwners >= 2 && (
                <InlineAlert variant="warning" title="Owners can sign in with a PIN.">Owners approve money and can reverse a close, and a PIN is easier to guess than a password. Consider two-step sign-in for owners too. Reversing a close still needs a password or a two-step code, never a PIN.</InlineAlert>
              )}
              {(s.pinRoles.includes("controller") || s.pinRoles.includes("winemaker")) && (
                <p className="small m0">Owners, controllers and winemakers approve money. If they use a PIN, consider two-step sign-in for them too.</p>
              )}
            </fieldset>
            <div className="signin-grp signin-sels">
              <Field label="Shortest PIN allowed" hint="PINs can be up to 6 numbers.">
                <select className="select" value={s.pinMinLength} disabled={dis} onChange={e => void save({ pinMinLength: Number(e.target.value) })}>
                  {[4, 5, 6].map(n => <option key={n} value={n}>{n} numbers{n === 4 ? " (default)" : ""}</option>)}
                </select>
              </Field>
              <Field label="Sign out a shared computer after" hint={"A “Still there?” warning shows 60 seconds before."}>
                <select className="select" value={s.sharedIdleMinutes} disabled={dis} onChange={e => void save({ sharedIdleMinutes: Number(e.target.value) })}>
                  {[2, 5, 10, 15, 30].map(n => <option key={n} value={n}>{n} minutes idle{n === 5 ? " (default)" : ""}</option>)}
                </select>
              </Field>
            </div>
            <div className="signin-computer" role="group" aria-labelledby="signin-computer-h">
              <div className="stack">
                <b id="signin-computer-h">This computer</b>
                <span className="small">{d.thisComputerShared ? "This is a shared computer." : "Not marked as shared."}</span>
                <span className="small muted">On a shared computer, the person's name shows in the header with a Switch user button, and it signs out when idle.</span>
              </div>
              <button className="btn" type="button" disabled={busy} onClick={() => void thisComputer(!d.thisComputerShared)}>{d.thisComputerShared ? "Stop treating it as shared" : "Mark this computer as shared"}</button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
